import { useCallback, useEffect, useState } from 'react';

type ElementSize = {
  width: number;
  height: number;
};

export function useElementSize<TElement extends HTMLElement>() {
  const [element, setElement] = useState<TElement | null>(null);
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
  });

  const ref = useCallback((nextElement: TElement | null) => {
    setElement(nextElement);
  }, []);

  useEffect(() => {
    if (!element) {
      return;
    }

    let animationFrameId: number | null = null;

    const updateSize = () => {
      const { height, width } = element.getBoundingClientRect();
      setSize({ width, height });
    };

    const scheduleUpdateSize = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        updateSize();
      });
    };

    scheduleUpdateSize();

    const resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }

      scheduleUpdateSize();
    });

    resizeObserver.observe(element);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      resizeObserver.disconnect();
    };
  }, [element]);

  return {
    setElement: ref,
    width: size.width,
    height: size.height,
  };
}
